import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const ThemeContext = createContext({ theme: "dark", resolvedTheme: "dark", setTheme: () => {} });

/**
 * Theme: "dark" | "light" | "system"
 * - "system" follows `prefers-color-scheme` and reacts to OS changes live.
 * - resolvedTheme is always "dark" or "light" — used to apply CSS.
 */
function getSystemTheme() {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem("mtt_theme") || "dark";
  });
  const [resolvedTheme, setResolvedTheme] = useState(() => {
    const t = (typeof window !== "undefined" && localStorage.getItem("mtt_theme")) || "dark";
    return t === "system" ? getSystemTheme() : t;
  });

  const setTheme = useCallback((t) => {
    setThemeState(t);
    if (typeof window !== "undefined") localStorage.setItem("mtt_theme", t);
  }, []);

  // React to OS theme changes when in system mode
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    if (theme !== "system") {
      setResolvedTheme(theme);
      return;
    }
    setResolvedTheme(getSystemTheme());
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const handler = (e) => setResolvedTheme(e.matches ? "light" : "dark");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.classList.toggle("theme-light", resolvedTheme === "light");
  }, [resolvedTheme]);

  return (
    <ThemeContext.Provider value={useMemo(() => ({ theme, resolvedTheme, setTheme }), [theme, resolvedTheme, setTheme])}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
